import React from 'react';
import { isEmpty } from 'lodash';
import { ICategory, IProductList } from '@/models';

import CategoryCard from './CategoryCard';
import ProductCard from './ProductCard';
import { useProducts } from '@/hooks/products/use-products';
import SkeletonLoader from './SkeletonLoader';
import { useTranslation } from 'react-i18next';
import { getLangCountry } from '@/helpers/languages';
import { fetchShippingCountriesList } from '@/fetchers/shipping';
import { useRouter } from 'next/router';
import useQueryParams from '@/hooks/useQueryParams';

interface Props {
  category: ICategory;
  products: (ICategory | IProductList)[];
  isLoading: boolean;
}

const SLICED_DEFAULT = 15;

const Products: React.FC<Props> = ({ category, products, isLoading }) => {
  const { t } = useTranslation();
  // const { data: { products = [] } = {}, isLoading } = useProducts({ category });
  const router = useRouter();
  const { lang, country } = getLangCountry(router.query);
  const { categories = [] } = router.query;
  if (isLoading) {
    return (
      <ul className="grid items-start grid-cols-2 gap-y-8 gap-x-2.5 xl:gap-y-24 xl:grid-cols-3 xl:gap-x-4">
        <SkeletonLoader />
      </ul>
    );
  }

  if (isEmpty(products)) {
    console.log('NO_PRODUCTS');
    return (
      <section className="section">
        <p>{t('NO_PRODUCTS')}</p>
      </section>
    );
  }

  const trackPageViewEvent = (groupped: (IProductList | ICategory)[]) => {
    const countries = fetchShippingCountriesList({ lang });
    const currentCountry = countries.find(c => {
      return c['country-code']?.toLowerCase() === country.toLowerCase();
    });

    //filter only products
    const products = groupped.filter((g): g is IProductList => {
      return 'selling_code' in g;
    });
    //split products into groups of SLICED_DEFAULT
    const productsGroups = [];
    for (let i = 0; i < products.length; i += SLICED_DEFAULT) {
      productsGroups.push(products.slice(i, i + SLICED_DEFAULT));
    }

    //populate dataLayer
    const dataLayer = window.dataLayer || [];
    productsGroups.forEach((group, index) => {
      const products = group.map((product, position) => {
        return {
          item_id: product.product_type + '-' + product.selling_code,
          item_name: product.name,
          discount:
            product.price?.sale_price && product.price?.price
              ? product.price?.price - product.price?.sale_price
              : 0,
          index: SLICED_DEFAULT * index + position + 1,
          item_brand: 'Uyn',
          item_category: Array.isArray(categories)
            ? categories.join('/')
            : categories,
          item_list_id: 'category_page',
          item_list_name: 'Category page',
          price: product.price?.sale_price || product.price?.price || 0,
          quantity: 1
        };
      });
      const event = {
        event: 'view_item_list',
        ecommerce: {
          item_list_id: 'category_page',
          item_list_name: 'Category page',
          currencyCode: currentCountry?.currency,
          items: products
        }
      };

      dataLayer.push({ ecommerce: null }, event);
    });
  };

  if (window.dataLayer) {
    trackPageViewEvent(products);
  }

  const trackClickEvent = (product: IProductList, position: number) => {
    const countries = fetchShippingCountriesList({ lang });
    const currentCountry = countries.find(c => {
      return c['country-code']?.toLowerCase() === country.toLowerCase();
    });

    window.dataLayer?.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer?.push({
      event: 'select_item',
      ecommerce: {
        currency: currentCountry?.currency,
        item_list_id: 'category_page',
        item_list_name: 'Category page',
        items: [
          {
            item_id: product.product_type + '-' + product.selling_code,
            item_name: product.name,
            discount:
              product.price?.sale_price && product.price?.price
                ? product.price?.price - product.price?.sale_price
                : 0,
            index: position + 1,
            item_brand: 'Uyn',
            item_category: Array.isArray(categories)
              ? categories.join('/')
              : categories,
            item_list_id: 'category_page',
            item_list_name: 'Category page',
            price: product.price?.sale_price || product.price?.price || 0,
            quantity: 1
          }
        ]
      }
    });
  };

  return (
    <ul className="grid items-start grid-cols-2 gap-y-8 gap-x-2.5 xl:gap-y-24 xl:grid-cols-3 xl:gap-x-4">
      {products.map((product, position) => {
        if ('category_name' in product) {
          return (
            <CategoryCard
              key={product.category_name}
              category={category}
              data={product}
            />
          );
        }
        console.log('product.colors', product.colors);
        return (
          <>
            {product.colors.map((color, index) => {
              return (
                <li key={product.slug + color}>
                  <ProductCard
                    data={product}
                    color={color}
                    _onClick={() =>
                      trackClickEvent(product as IProductList, position)
                    }
                  />
                </li>
              );
            })}
          </>
        );
      })}
    </ul>
  );
};

export default Products;
